import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormCell, FormSection, FormRow as Row, TextInput, Toggle } from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { profileFormMachine } from '../profileForm.machine';
import { EmailFormValues, emailSchema } from '../types';

const TAGS = CT.profile.contactInfo.email.form;
interface FormProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  addEmail: (email: EmailFormValues) => void;
  editEmail: (email: EmailFormValues) => void;
}

export const EmailForm = (props: FormProps) => {
  const { addEmail, editEmail, actor } = props;
  const emailAddressForm = useSelector(actor, (state) => state.context.emailAddressForm);
  const create = useSelector(actor, (state) => state.context.modal.create);

  const methods = useCustomForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: emailAddressForm,
  });
  const { handleSubmit } = methods;

  // watch + useEffect for manual 'Async' on dependent Select
  const onSubmitError = (err: unknown) => console.debug('Email form validation error:', err);
  const onSubmitSuccess = (values: EmailFormValues) => {
    if (create) {
      addEmail(values);
    } else {
      editEmail(values);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className="flex flex-col gap-5"
        data-cy={TAGS.form}
        onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}
        id="phone-number-form"
      >
        <FormSection componentsPerLine={1}>
          <FormCell span={8}>
            <Row>
              <TextInput
                name={'emailAddress'}
                cyTag={TAGS.emailAddressInput}
                label={'Email address'}
              />
            </Row>
          </FormCell>
          <FormCell span={4}>
            <Row>
              {/* [MCV-861] Single label on the Toggle itself, rather than a
                  redundant outer `<Label>Primary?</Label>` + description that
                  rendered two adjacent question marks ("Primary? Is this your
                  primary email?") and was confusing in QA. */}
              <Toggle
                name={'primaryFlag'}
                label={'Primary email'}
                description={'Make this the primary email on the account'}
                cyTag={TAGS.isPrimaryToggle}
              />
            </Row>
          </FormCell>
        </FormSection>
        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
